import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectorPermissions } from '@/stores/auth';
import Page from '@/components/page';
import PageTitle from '@/components/page-title';
import AuthLayout from './auth/_layout';
import LoginPage from './auth/login';
import MainLayout from './main/_layout';
import ParentSearchPage from './main/parent/search';
import ParentEditPage from './main/parent/edit';
import ParentViewPage from './main/parent/view';
import ChildSeachPage from './main/child/search';
import ChildEditPage from './main/child/edit';
import ChildViewPage from './main/child/view';
import ChildPhotoManager from './main/child/photo-manager';
import SiblingGroupEditPage from './main/sibling-group/edit';
import SiblingGroupViewPage from './main/sibling-group/view';
import AddChildPage from './main/sibling-group/add-child';
import ChangePasswordPage from './main/user/change-password';
import ParentQuickViewPage from './main/parent/quick-view';
import ChildQuickViewPage from './main/child/quick-view';
import AddSiblingGroup from './main/sibling-group/add-sibling-group';
import AddChildrenPage from './main/sibling-group/add-children';

const EmptyPage = ({ title }) => (
  <Page breadcrumb={[title]}>
    <PageTitle label={title} />
    <div style={{ textAlign: 'center', paddingBlock: 50 }}>{title} Page</div>
  </Page>
);

const Pages = () => {
  const permissions = useSelector(selectorPermissions);
  const [updateSignal, setUpdateSignal] = useState(0);

  const triggerUpdate = () => {
    setUpdateSignal((prevSignal) => prevSignal + 1);
  };

  return (
    <Routes>
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="*?" element={<Navigate to="login" replace />} />
      </Route>

      <Route path="/" element={<MainLayout />}>
        {permissions.viewParents && (
          <Route path="parent">
            <Route index element={<Navigate to="search" replace />} />
            <Route path="search" element={<ParentSearchPage />} />
            <Route path="quick-view">
              <Route path="home-study" element={<ParentQuickViewPage />} />
              <Route path="open-inquiries" element={<ParentQuickViewPage />} />
            </Route>
            {permissions.writeParent && <Route path="new" element={<ParentEditPage />} />}
            <Route path=":parentId">
              <Route path="view/*?/*" element={<ParentViewPage />} />
              {permissions.writeParent && <Route path="edit" element={<ParentEditPage />} />}
              <Route path="*?" element={<Navigate to="view" replace />} />
            </Route>
          </Route>
        )}

        <Route path="child">
          <Route index element={<Navigate to="search" replace />} />
          <Route path="search" element={<ChildSeachPage />} />
          <Route path="quick-view">
            <Route path="by-status" element={<ChildQuickViewPage />} />
          </Route>
          {permissions.writeChild && <Route path="new" element={<ChildEditPage />} />}
          <Route path=":childId">
            <Route path="view/*?/*" element={<ChildViewPage />} />
            {permissions.writeChild && <Route path="edit" element={<ChildEditPage />} />}
            {permissions.writeChild && <Route path="manage-photos" element={<ChildPhotoManager />} />}
            <Route path="*?" element={<Navigate to="view" replace />} />
          </Route>
        </Route>

        {permissions.writeChild && (
          <Route path="sibling-group">
            <Route index element={<Navigate to="new" replace />} />
            <Route path="new" element={<AddSiblingGroup />} />
            <Route path=":groupId">
              <Route path="edit" element={<SiblingGroupEditPage triggerUpdate={triggerUpdate} />} />
              <Route path="view" element={<SiblingGroupViewPage updateSignal={updateSignal} />} />
              <Route path="add-child" element={<AddChildPage />} />
              <Route path="add-children" element={<AddChildrenPage />} />
              <Route path="*?" element={<Navigate to="view" replace />} />
            </Route>
          </Route>
        )}

        <Route path="user">
          <Route path="change-password" element={<ChangePasswordPage />} />
          <Route path="*?" element={<Navigate to="change-password" replace />} />
        </Route>

        <Route path="dashboards/select" element={<EmptyPage title="Dashboard" />} />
        {permissions.events && <Route path="events" element={<EmptyPage title="Events" />} />}
        {permissions.reports && <Route path="reports/newreports.aspx" element={<EmptyPage title="New Reports" />} />}
        {permissions.referralManagement && (
          <Route path="referralmanagement" element={<EmptyPage title="Referral Management" />} />
        )}
        {permissions.admin && <Route path="admin" element={<EmptyPage title="Admin" />} />}
        <Route
          path="*?"
          Component={() => {
            window.location.href = '/dashboards/select';
          }}
        />
      </Route>
    </Routes>
  );
};

export default Pages;
